import { useEffect, useRef, useState } from "react";
import { List, RadioButton, Searchbar, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import EmptyState from "../EmptyView";
import Loader from "../Loader";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingBox } from "./components/OnboardingBox";
import { groupExams } from "./helpers";

type Props = {
	title: string;
	description?: string;
};

const SEARCH_THRESHOLD = 5;

export const OnboardingExamScreen = ({ title, description }: Props) => {
	const theme = useAppTheme();
	const { exams, filteredExams, selectedExamId, onSelectExam } =
		useOnboarding();
	const [searchValue, setSearchValue] = useState("");

	const onSelectExamRef = useRef(onSelectExam);
	const filteredExamsRef = useRef(filteredExams);

	useEffect(() => {
		if (filteredExamsRef.current.length === 1 && !selectedExamId) {
			onSelectExamRef.current(filteredExamsRef.current[0].exam.id);
		}
	}, [selectedExamId]);

	const sections = groupExams(filteredExams)
		.map((section) => ({
			...section,
			data: searchValue
				? section.data.filter((item) =>
						item.name.toLowerCase().includes(searchValue.toLowerCase()),
					)
				: section.data,
		}))
		.filter((section) => section.data.length > 0);

	if (!exams) {
		return <Loader />;
	}

	const showSearch = filteredExams.length > SEARCH_THRESHOLD;
	const showSectionLabels = sections.length > 1;

	return (
		<OnboardingBox>
			<Box gap={14}>
				<Text variant="titleLarge">{title}</Text>
				{description ? <Text variant="bodyMedium">{description}</Text> : null}
			</Box>
			{showSearch ? (
				<Searchbar
					value={searchValue}
					onChangeText={setSearchValue}
					placeholder="Search exams..."
				/>
			) : null}
			{sections.length === 0 ? (
				<EmptyState
					title="No exams found"
					subtitle="Try adjusting your search query"
					cta="Clear search"
					handleAction={() => setSearchValue("")}
				/>
			) : (
				<Box gap={16}>
					{sections.map((section) => (
						<Box key={section.title} gap={4}>
							{showSectionLabels ? (
								<Text
									variant="labelSmall"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{section.title}
								</Text>
							) : null}
							{section.data.map((item) => (
								<List.Item
									key={item.id}
									onPress={() => onSelectExam(item.id)}
									title={item.name}
									titleNumberOfLines={2}
									style={{ paddingHorizontal: 0 }}
									left={() => (
										<List.Icon
											icon={() => (
												<RadioButton.Android
													status={
														selectedExamId === item.id ? "checked" : "unchecked"
													}
													onPress={() => onSelectExam(item.id)}
													value={item.id}
												/>
											)}
										/>
									)}
								/>
							))}
						</Box>
					))}
				</Box>
			)}
		</OnboardingBox>
	);
};
